import React, { useContext } from "react";
import HorizontalAddonCard from "~/assets/js/components/AddonCard/HorizontalAddonCard";
import PropTypes from "prop-types";
import Button from "~/assets/js/components/Button/";
import { ShoppingBag, ArrowLeft } from "react-feather";
import {
    ProductsDetailsContext,
    ITEM_TYPE,
    ACTIONS,
    UPDATE_TYPE,
} from "../../ProductsProvider";
import {
    formatPrice,
    calculateDressingDisplayPrice,
    selectedIngredientsQuantity,
} from "~/assets/js/Format";
import i18next from "i18next";
import useViewport from "~/assets/js/Helpers/hooks/useViewport";
import useThereIsMoreToScroll from "~/assets/js/Helpers/hooks/useThereIsMoreToScroll";
import ThereIsMore from "../../components/ThereIsMore";
import ProductModifiedNotification from "../../components/ProductModifiedNotification";
import { GREEN_CLUB_URL } from "~/assets/js/global/greenClub";

const OverviewIngredients = ({ handleClickBack, handleClose, discounted }) => {
    const {
        state,
        fullPrice,
        updateProductState,
        isSubmitting,
        submit,
        inStock,
        isModified,
        greenClubMembershipPrice,
        selectGreenClubPrice,
        isBowl,
        isSalad,
    } = useContext(ProductsDetailsContext);
    const { dressings: selectedDressings, extras, basic: basics } = state;
    const product = pottsalatProduct;

    const { isDesktop } = useViewport();

    const { thereIsMore, scrollRef, handleScroll } = useThereIsMoreToScroll(
        100,
        [selectedDressings, extras, basics]
    );

    const increment = (product_id, itemType) => {
        const data = { product_id, update: UPDATE_TYPE.INCREMENT, itemType };
        return updateProductState({ type: ACTIONS.UPDATE, payload: data });
    };

    const decrement = (product_id, quantity, itemType) => {
        const data = { product_id, itemType };

        if (quantity === 1) {
            return updateProductState({
                type: ACTIONS.REMOVE,
                payload: data,
            });
        }

        data.update = UPDATE_TYPE.DECREMENT;
        return updateProductState({ type: ACTIONS.UPDATE, payload: data });
    };

    const handleSubmit = () => {
        handleClose();
        submit();
    };

    return (
        <div id="overview-ingredients">
            {isDesktop && (
                <div className="overview-header">
                    <div className="overview-header__left">
                        <div className="img-container">
                            <img
                                src={product.images[0].src}
                                alt={product.title}
                            />
                        </div>
                        <div className="title">
                            <p className="title__small">
                                {i18next.t("basicIngredients.overview")}
                            </p>
                            <p className="title__big">{product.title}</p>
                        </div>
                    </div>
                    <Button
                        loading={isSubmitting}
                        label={
                            selectGreenClubPrice
                                ? i18next.t(
                                      "productDetails.greenClub.onboardingPopup.cta"
                                  )
                                : i18next.t("productInfo.addToCart")
                        }
                        icon={
                            !selectGreenClubPrice ? <ShoppingBag /> : undefined
                        }
                        loadingLabel=""
                        size="SM"
                        customStyle={!inStock ? "disabled" : ""}
                        href={selectGreenClubPrice ? GREEN_CLUB_URL : false}
                        onClick={handleSubmit}
                    />
                </div>
            )}

            <div
                className={
                    isDesktop ? "overview-wrapper" : "ingredients-wrapper"
                }
                ref={scrollRef}
                onScroll={handleScroll}>
                {isModified && <ProductModifiedNotification />}
                {/* EXTRA INGREDIENTS */}
                {extras.length > 0 && (
                    <>
                        <p className="ingredients-title">
                            {i18next.t("extraIngredients.selectedOverview", {
                                count: selectedIngredientsQuantity(extras),
                            })}
                        </p>
                        {extras.map((extra) => {
                            const props = {
                                name: extra.name,
                                image: extra.images.normal,
                                price: formatPrice(
                                    extra.price * extra.quantity
                                ),
                                details: extra.details,
                                quantity: extra.quantity,
                                increment: () =>
                                    increment(
                                        extra.product_id,
                                        ITEM_TYPE.EXTRA
                                    ),
                                decrement: () =>
                                    decrement(
                                        extra.product_id,
                                        extra.quantity,
                                        ITEM_TYPE.EXTRA
                                    ),
                            };
                            return (
                                <HorizontalAddonCard
                                    key={extra.product_id}
                                    {...props}
                                />
                            );
                        })}
                    </>
                )}
                {/* DRESSINGS */}
                {selectedDressings.length > 0 && (
                    <>
                        <p className="ingredients-title">
                            {i18next.t("basicIngredients.dressingsTitle", {
                                quantity:
                                    selectedIngredientsQuantity(
                                        selectedDressings
                                    ),
                            })}
                        </p>
                        {selectedDressings.map((dressing) => {
                            const price = calculateDressingDisplayPrice(
                                dressing,
                                selectedDressings
                            );

                            const dressingsProps = {
                                name: dressing.name,
                                image: dressing.image,
                                description:
                                    price === "free"
                                        ? i18next.t(
                                              "dressings.firstDressingFree",
                                              {
                                                  productName:
                                                      state.productName,
                                              }
                                          )
                                        : null,
                                price: price !== "free" ? price : null,
                                details: dressing.details,
                                quantity: dressing.quantity,
                                increment: () =>
                                    increment(
                                        dressing.product_id,
                                        ITEM_TYPE.DRESSINGS
                                    ),
                                decrement: () =>
                                    decrement(
                                        dressing.product_id,
                                        dressing.quantity,
                                        ITEM_TYPE.DRESSINGS
                                    ),
                            };

                            return (
                                <HorizontalAddonCard
                                    {...dressingsProps}
                                    key={dressing.product_id}
                                />
                            );
                        })}
                    </>
                )}
                {/* BASIC INGREDIENTS */}
                <p className="ingredients-title">
                    {i18next.t("basicIngredients.quantityTitle", {
                        quantity: selectedIngredientsQuantity(basics),
                    })}
                </p>
                {basics.map((basic) => {
                    const displayPrice = basic.quantity > 1;
                    const ingredientProps = {
                        name: basic.name,
                        image: basic.images.normal,
                        description: !displayPrice
                            ? i18next.t("basicIngredients.includesOne", {
                                  productName: isBowl
                                      ? i18next.t("basicIngredients.inBowl")
                                      : isSalad
                                      ? i18next.t("basicIngredients.inSalad")
                                      : i18next.t("basicIngredients.inSoup"),
                              })
                            : null,
                        price: displayPrice
                            ? formatPrice(basic.price * (basic.quantity - 1))
                            : null,
                        details: basic.details,
                        quantity: basic.quantity,
                        tags: basic.tags,
                        increment: () =>
                            increment(basic.product_id, ITEM_TYPE.BASIC),
                        decrement: () =>
                            decrement(
                                basic.product_id,
                                basic.quantity,
                                ITEM_TYPE.BASIC
                            ),
                    };

                    return (
                        <HorizontalAddonCard
                            {...ingredientProps}
                            isBasic
                            key={basic.product_id}
                        />
                    );
                })}
                {thereIsMore && <ThereIsMore />}
            </div>
            <div className="overview-footer">
                <span className="back" onClick={handleClickBack}>
                    <ArrowLeft size={18} />
                    {i18next.t("header.back")}
                </span>
                <div className="right-side">
                    <div className="price-container">
                        <p className="price">
                            {discounted ? greenClubMembershipPrice : fullPrice}
                        </p>
                        <p className="tax">
                            {i18next.t("productInfo.taxRate", {
                                rate: product.taxRate,
                            })}
                        </p>
                    </div>
                    {selectGreenClubPrice ? (
                        <Button
                            label={
                                isDesktop
                                    ? i18next.t(
                                          "productDetails.greenClub.onboardingPopup.cta"
                                      )
                                    : ""
                            }
                            icon={isDesktop ? undefined : <ShoppingBag />}
                            loadingLabel=""
                            size="SM"
                            customStyle={isDesktop ? "" : "sticky-footer-btn"}
                            href={GREEN_CLUB_URL}
                        />
                    ) : (
                        <Button
                            loading={isSubmitting}
                            label={
                                isDesktop
                                    ? i18next.t("productInfo.addToCart")
                                    : ""
                            }
                            icon={<ShoppingBag />}
                            loadingLabel=""
                            size="SM"
                            onClick={handleSubmit}
                            customStyle={`${
                                isDesktop ? "" : "sticky-footer-btn"
                            } ${!inStock ? "disabled" : ""}`}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

OverviewIngredients.propTypes = {
    handleClickBack: PropTypes.func,
    handleClose: PropTypes.func,
    discounted: PropTypes.bool,
};

export default OverviewIngredients;
